import * as React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'

interface Props {
  item: Partial<IncidentForm>
  onClick: (item: Partial<IncidentForm>) => void
  onDelete?: (id: string) => void
}

const DraftIncidentCard = ({ item, onClick, onDelete }: Props) => {
  const i18n = useI18n()
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)
  const places = useReducer(LocationStore.store, (s) => s.places)

  const manif = manifestations.find((m) => m.id === item.manifestationId)
  const acts = (item.familles || [])
    .map((ac) => activities.find((a) => a.id === ac))
    .filter(Boolean) as FamilleResponse[]
  const space = places.find((m) => m.id === item.space)
  const location = places.find((m) => m.id === item.location)
  const subLocation = places.find((m) => m.id === item.subLocation)

  const loc = [!!space && space.libelle, !!location && location.libelle, !!subLocation && subLocation.libelle]
    .filter(Boolean)
    .join(' - ')

  return (
    <Item key={item.id} activeOpacity={0.9} onPress={() => onClick(item)}>
      <ItemBloc>
        <Manif numberOfLines={2} ellipsizeMode="tail">
          {manif ? manif.libelle : i18n.t('screens.incident.MANIF.empty')}
        </Manif>
        <Location>{loc}</Location>

        {!!onDelete && !isPhone && (
          <Delete activeOpacity={0.9} onPress={() => onDelete(item.id || '')}>
            <Icon name="delete" size={24} color={Theme.colors.primary} />
          </Delete>
        )}
      </ItemBloc>

      <Separator />

      <ItemBloc>
        {!!item.isInfrastructure && (
          <Info>{i18n.t(`screens.incident.TYPE.${item.isInfrastructure.toLowerCase()}`)}</Info>
        )}
        {acts.length > 0 && (
          <Activity numberOfLines={2} ellipsizeMode="tail">
            {acts.map((a) => a.libelle).join(' / ')}
          </Activity>
        )}
        {!!item.creationDate && (
          <Info>{i18n.t('screens.incident.START.date', { date: new Date(item.creationDate) })}</Info>
        )}
      </ItemBloc>
    </Item>
  )
}

export default DraftIncidentCard

const Item = styled(TouchableOpacity)`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.secondaryActive};
  border-radius: 6px;
  margin-bottom: 10px;
  position: relative;
  ${(props) => props.theme.breakpoint !== 'phone' && `height: 230px;`}
`

const ItemBloc = styled(View)`
  padding: 16px 24px;
  justify-content: center;
`

const Manif = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Location = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 14px;
`

const Activity = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  font-size: 16px;
`

const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
`

const Delete = styled(TouchableOpacity)`
  position: absolute;
  right: 20px;
  padding: 10px;
`
